import { graphql, type HeadProps, type PageProps } from 'gatsby'
import isNil from 'lodash/isNil'
import React from 'react'

import Newsletter from '@/components/centered-call-to-action/centered-call-to-action'
import Header from '@/components/header/header'
import HeaderBackground from '@/components/header/header-background'
import HeaderBackgroundV2 from '@/components/header/header-background-v2'
import HeaderBackgroundV3 from '@/components/header/header-background-v3'
import HeaderImage from '@/components/header/header-image'
import MenuPage from '@/components/navigation/menu-page'
import SectionBlocks from '@/components/section-blocks/section-blocks'
import { Microdata } from '@/components/seo/microdata'
import Seo from '@/components/seo/seo'
import { ColorProvider } from '@/contexts/color.provider'
import Layout from '@/layouts/layout'
import { NavigationItem } from '@/utils/get-navigation-label'

export type DataProps = {
  datoCmsPctPage: Queries.PageQuery['datoCmsPctPage']
  allDatoCmsPctPost: Queries.PageQuery['allDatoCmsPctPost']
  allDatoCmsPctInterview: Queries.PageQuery['allDatoCmsPctInterview']
  allDatoCmsPctPaper: Queries.PageQuery['allDatoCmsPctPaper']
  allDatoCmsPctDialog: Queries.PageQuery['allDatoCmsPctDialog']
  allDatoCmsPctProject: Queries.PageQuery['allDatoCmsPctProject']
  allDatoCmsPctEpisode: Queries.PageQuery['allDatoCmsPctEpisode']
  allDatoCmsPctExternalContent: Queries.PageQuery['allDatoCmsPctExternalContent']
  allDatoCmsPctPage: Queries.PageQuery['allDatoCmsPctPage']
  allDatoCmsPctEvent: Queries.PageQuery['allDatoCmsPctEvent']
}

export const Head: React.FC<HeadProps<DataProps>> = ({
  data: { datoCmsPctPage },
  location
}) => {
  return (
    <>
      <Seo data={datoCmsPctPage} location={location} />
      <Microdata data={datoCmsPctPage} location={location} />
      {datoCmsPctPage?.noIndex === true && (
        <meta name="robots" content="noindex" />
      )}
    </>
  )
}

const PageTemplate: React.FC<PageProps<DataProps, PageCreatorPageContext>> = ({
  data: {
    datoCmsPctPage,
    allDatoCmsPctPost,
    allDatoCmsPctInterview,
    allDatoCmsPctPaper,
    allDatoCmsPctDialog,
    allDatoCmsPctProject,
    allDatoCmsPctEpisode,
    allDatoCmsPctPage,
    allDatoCmsPctExternalContent,
    allDatoCmsPctEvent
  },
  pageContext,
  location
}) => {
  if (datoCmsPctPage === null) {
    return <></>
  }
  const nodes = [
    ...allDatoCmsPctPost.nodes,
    ...allDatoCmsPctInterview.nodes,
    ...allDatoCmsPctProject.nodes,
    ...allDatoCmsPctPaper.nodes,
    ...allDatoCmsPctDialog.nodes,
    ...allDatoCmsPctEpisode.nodes,
    ...allDatoCmsPctPage.nodes,
    ...allDatoCmsPctExternalContent.nodes,
    ...allDatoCmsPctEvent.nodes
  ]
  const {
    headerStyle,
    externalName,
    headline,
    treeParent,
    colour,
    excerpt,
    topic,
    image,
    headlineImage,
    headerIsDark,
    showSubscribeBulletin,
    hideNavigation
  } = datoCmsPctPage
  const showNavigation = !(hideNavigation ?? false)
  const isHeaderDefault = isNil(headerStyle) || headerStyle === 'default'
  const isHeaderImage = headerStyle === 'image'
  const isHeaderBackgroundV1 = headerStyle === 'background1'
  const isHeaderBackgroundV2 = headerStyle === 'background2'
  const isHeaderBackgroundV3 = headerStyle === 'background3'
  return (
    <Layout location={location} showNavigation={showNavigation}>
      <ColorProvider colour={colour}>
        {isHeaderDefault && (
          <Header
            title={externalName ?? ''}
            subtitle={headline ?? ''}
            parent={treeParent}
          />
        )}
        {isHeaderImage && (
          <HeaderImage
            title={externalName ?? ''}
            subtitle={headline ?? ''}
            logo={image}
            icon={headlineImage}
            isDark={headerIsDark ?? false}
          />
        )}
        {isHeaderBackgroundV1 && (
          <HeaderBackground
            image={image}
            title={externalName ?? ''}
            headline={headline ?? ''}
            excerpt={excerpt ?? ''}
          />
        )}
        {isHeaderBackgroundV2 && (
          <HeaderBackgroundV2
            image={image}
            title={externalName ?? ''}
            headline={headline ?? ''}
          />
        )}
        {isHeaderBackgroundV3 && (
          <HeaderBackgroundV3
            image={image}
            title={externalName ?? ''}
            topic={topic}
          />
        )}
        <MenuPage
          blocks={datoCmsPctPage.blocks as unknown as NavigationItem[]}
        />
        <SectionBlocks
          blocks={datoCmsPctPage.blocks}
          animated={datoCmsPctPage.animated ?? false}
          pageContext={pageContext}
          nodes={nodes}
        />
        {showSubscribeBulletin === true && <Newsletter />}
      </ColorProvider>
    </Layout>
  )
}

export default PageTemplate

export const PageQuery = graphql`
  query Page($id: String = "", $allNodesIds: [String] = "") {
    datoCmsPctPage(id: { eq: $id }) {
      ...PctPage
    }

    allDatoCmsPctPost(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $allNodesIds } }
    ) {
      nodes {
        ...ResultPost
      }
    }
    allDatoCmsPctInterview(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $allNodesIds } }
    ) {
      nodes {
        ...ResultInterview
      }
    }
    allDatoCmsPctPaper(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $allNodesIds } }
    ) {
      nodes {
        ...ResultPaper
      }
    }
    allDatoCmsPctDialog(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $allNodesIds } }
    ) {
      nodes {
        ...ResultDialog
      }
    }
    allDatoCmsPctProject(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $allNodesIds } }
    ) {
      nodes {
        ...ResultProject
      }
    }
    allDatoCmsPctEpisode(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $allNodesIds } }
    ) {
      nodes {
        ...ResultEpisode
      }
    }
    allDatoCmsPctPage(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $allNodesIds } }
    ) {
      nodes {
        ...ResultPage
      }
    }

    allDatoCmsPctExternalContent(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $allNodesIds } }
    ) {
      nodes {
        ...ResultExternalContent
      }
    }
    allDatoCmsPctEvent(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $allNodesIds } }
    ) {
      nodes {
        ...ResultEvent
      }
    }
  }

  fragment PctPage on DatoCmsPctPage {
    __typename
    originalId
    meta {
      firstPublishedAt
      publishedAtTimestamp: publishedAt(formatString: "X")
    }
    slug
    excerpt
    topic {
      ...TopicReference
    }
    externalName
    headline
    headerStyle
    headerIsDark
    headlineImage {
      gatsbyImageData
      url
    }
    image {
      gatsbyImageData(
        layout: FULL_WIDTH
        imgixParams: { ar: "16:9", fit: "crop", crop: "focalpoint" }
      )
      alt
    }
    seoMetaTags {
      ...GatsbyDatoCmsSeoMetaTags
    }
    seo {
      title
      description
      image {
        url
      }
    }
    colour {
      ... on DatoCmsPctColour {
        colour {
          hex
        }
        isLight
        codeId
      }
    }
    treeParent {
      ... on DatoCmsPctPage {
        slug
        internalName
      }
    }
    showSubscribeBulletin
    animated
    noIndex
    hideNavigation
    blocks {
      ... on DatoCmsFeaturedLeader {
        ...FeaturedLeader
      }
      ... on DatoCmsFeaturedPerson {
        ...FeaturedPerson
      }
      ... on DatoCmsFeaturedContent {
        ...FeaturedContent
      }
      ... on DatoCmsFeaturedPaper {
        ...SectionFeaturedPaper
      }
      ... on DatoCmsFeaturedMultipleContent {
        ...SectionFeaturedContents
      }
      ... on DatoCmsFeaturedMultiplePaper {
        ...SectionFeaturedPapers
      }
      ... on DatoCmsGallery {
        ...Gallery
      }
      ... on DatoCmsSection {
        ...Section
      }
      ... on DatoCmsFeaturedStatic {
        ...FeaturedStatic
      }
      ... on DatoCmsSlice {
        ...SectionSlice
      }
      ... on DatoCmsSliceNavigation {
        ...SectionSliceNavigation
      }
      ... on DatoCmsForm {
        ...FormBlock
      }
      ... on DatoCmsSnippet {
        ...Snippet
      }
      ... on DatoCmsSeparatorSection {
        ...SeparatorSection
      }
      ... on DatoCmsFeatureSection {
        ...FeatureSection
      }
      ... on DatoCmsUseCaseSection {
        ...UseCaseSection
      }
      ... on DatoCmsShowcaseSection {
        ...ShowcaseSection
      }
      ... on DatoCmsBenefitSection {
        ...BenefitSection
      }
      ... on DatoCmsLogo {
        ...Logos
      }
      ... on DatoCmsAgendaBlock {
        ...AgendaBlock
      }
      ... on DatoCmsFeaturedCta {
        ...FeaturedCta
      }
    }
  }
`
